import { useCallback, useMemo, useRef } from 'react';
import { useMutation } from 'data-fetching-client';
import PortalIdParser from 'PortalIdParser';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import I18n from 'I18n';
import { Button } from 'foundations-components/transitional/components/Button';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { DialogFooter } from 'foundations-components/transitional/components/DialogFooter';
import { DialogHeader } from 'foundations-components/transitional/components/DialogHeader';
import { Form } from 'foundations-components/transitional/components/Form';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Modal } from 'foundations-components/transitional/components/Modal';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { addErrorAlert, addSuccessAlert } from '../../../app/utils/alerting';
import { TrackingScreen, trackBetaEnrollmentChange } from '../../../app/utils/tracking';
import { GET_ROLLOUT_PRODUCT_UPDATES, GET_ROLLOUT_PRODUCT_UPDATE_BY_ROLLOUT_ID, GET_ROLLOUT_PRODUCT_UPDATE_BY_UPDATE_ID, LEAVE_BETA, updateProductUpdateCache } from '../../queries';
import { EnrollTypes, EnrollmentStatuses } from '../../constants';
import { FeedbackBtnClickTrackerContext } from '../../contexts/Tracking';
import BetaFeedbackFormSection from '../beta-feedback-modal/BetaFeedbackFormSection';
import { useRawParamsForListRolloutProductUpdates } from '../../hooks/useRawParamsForListRolloutProductUpdates';
import { categoryToSubscreenMap } from '../../../app/utils/tracking/utils';
import { getRequestParamsForGetRolloutProductUpdates } from '../../utils';
import useFetchOrgData from '../../hooks/useFetchOrgData';
import { useAppSelector } from '../../../app/indexReducer';
import useFeedbackRateLimit from '../../hooks/useFeedbackRateLimit';
import { selectAuthenticatedUser } from '../../../app/auth/AuthSelectors';
import handleError from '../../../app/utils/handleError';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LeaveBetaDialog = ({
  userId,
  rolloutId,
  productUpdateId,
  title,
  untranslatedTitle,
  gate,
  enrollmentStatus,
  enrollType,
  betaAccessType,
  onClose
}) => {
  const user = useAppSelector(selectAuthenticatedUser);
  const portalId = PortalIdParser.get();
  const {
    category
  } = useRawParamsForListRolloutProductUpdates();
  const feedbackFormSectionRef = useRef(null);
  const {
    updateRateLimit
  } = useFeedbackRateLimit(user === null || user === void 0 ? void 0 : user.user_id);
  const {
    portalsInOrgCount
  } = useFetchOrgData();
  const variables = useMemo(() => ({
    rolloutId,
    portalId,
    enrollId: enrollmentStatus === EnrollmentStatuses.USER ? userId : portalId,
    enrollType: enrollmentStatus === EnrollmentStatuses.USER ? EnrollTypes.USERS_ONLY : enrollmentStatus === EnrollmentStatuses.PORTAL ? EnrollTypes.PORTALS_ONLY : EnrollTypes.ORGANIZATIONS_ONLY,
    betaAccessType
  }), [enrollmentStatus, portalId, rolloutId, userId, betaAccessType]);
  const [leaveBeta, {
    loading: leaveBetaLoading,
    error: leaveBetaError
  }] = useMutation(LEAVE_BETA, {
    refetchQueries: [{
      query: GET_ROLLOUT_PRODUCT_UPDATES,
      variables: getRequestParamsForGetRolloutProductUpdates({
        app: null,
        calendarTimestamp: null,
        endDate: null,
        impactLevels: null,
        isFavorite: null,
        limit: null,
        offset: null,
        product: null,
        query: null,
        recommended: null,
        rolloutState: null,
        startDate: null,
        type: null,
        view: null,
        category: 'your-betas',
        betaStatus: 'enrolled'
      })
    }, {
      query: GET_ROLLOUT_PRODUCT_UPDATE_BY_ROLLOUT_ID,
      variables: {
        id: rolloutId
      }
    }, {
      query: GET_ROLLOUT_PRODUCT_UPDATE_BY_UPDATE_ID,
      variables: {
        id: productUpdateId
      }
    }],
    update: cache => {
      updateProductUpdateCache(cache, {
        productUpdateId: `${productUpdateId}`,
        rolloutId: `${rolloutId}`,
        nextEnrollmentStatus: 'NOT_ENROLLED'
      });
    },
    onError: err => {
      handleError(err);
      addErrorAlert(enrollmentStatus === EnrollmentStatuses.USER ? 'app.betas.errors.problemLeavingYou' : enrollmentStatus === EnrollmentStatuses.PORTAL ? 'app.betas.errors.problemLeavingAccount' : 'app.betas.errors.problemLeavingOrganization');
      onClose();
    },
    onCompleted: () => {
      var _feedbackFormSectionR;
      trackBetaEnrollmentChange({
        title,
        untranslatedTitle,
        gateName: gate,
        currentState: enrollmentStatus,
        desiredState: EnrollmentStatuses.NOT_ENROLLED
      });
      addSuccessAlert('app.betas.success.youLeftDescription', title);
      (_feedbackFormSectionR = feedbackFormSectionRef.current) === null || _feedbackFormSectionR === void 0 || _feedbackFormSectionR.onSubmit();
      onClose();
    }
  });
  const handleLeave = useCallback(evt => {
    evt.preventDefault();
    void leaveBeta({
      variables
    });
  }, [variables, leaveBeta]);
  return /*#__PURE__*/_jsx(Modal, {
    children: /*#__PURE__*/_jsxs(Form, {
      onSubmit: handleLeave,
      children: [/*#__PURE__*/_jsx(DialogCloseButton, {
        onClick: onClose
      }), /*#__PURE__*/_jsx(DialogHeader, {
        children: /*#__PURE__*/_jsx(Text, {
          as: "h2",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.betas.leave.title"
          })
        })
      }), /*#__PURE__*/_jsxs(DialogBody, {
        children: [/*#__PURE__*/_jsx(View, {
          marginBlockEnd: "400",
          children: /*#__PURE__*/_jsx(Paragraph, {
            children: enrollType === EnrollTypes.ORGANIZATIONS_ONLY ? /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: "app.betas.leave.feedbackQuestionOrg",
              options: {
                betaName: title,
                portalsInOrg: I18n.SafeString(portalsInOrgCount.toString())
              }
            }) : /*#__PURE__*/_jsx(FormattedHTMLMessage, {
              message: "app.betas.leave.feedbackQuestion",
              options: {
                betaName: title
              }
            })
          })
        }), /*#__PURE__*/_jsx(FeedbackBtnClickTrackerContext.Provider, {
          value: {
            screen: TrackingScreen.PRODUCT_UPDATES,
            subscreen: categoryToSubscreenMap[category !== null && category !== void 0 ? category : 'all'],
            origin: 'leave beta',
            rolloutState: null,
            untranslatedPUTitle: untranslatedTitle
          },
          children: /*#__PURE__*/_jsx(BetaFeedbackFormSection, {
            ref: feedbackFormSectionRef,
            rolloutId: rolloutId,
            productUpdateId: productUpdateId,
            onCompletedCb: () => {
              updateRateLimit(productUpdateId);
            }
          })
        })]
      }), /*#__PURE__*/_jsxs(DialogFooter, {
        children: [/*#__PURE__*/_jsx(LoadingButton, {
          use: "primary",
          failed: Boolean(leaveBetaError),
          loading: leaveBetaLoading,
          type: "submit",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.betas.leave.action"
          })
        }), /*#__PURE__*/_jsx(Button, {
          onClick: onClose,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app.betas.leave.cancel"
          })
        })]
      })]
    })
  });
};
export default LeaveBetaDialog;